/**
 * rehypeStripBlockWhitespace — removes whitespace-only text nodes that sit
 * between block-level elements in the hast tree.
 *
 * Why this is needed:
 *   remark-rehype inserts a literal "\n" text node between every sibling
 *   block element (<p>\n<h2>\n<ul>...). In CSS block flow, an anonymous
 *   inline box between two block-level siblings completely prevents vertical
 *   margin collapsing — regardless of its height. So <p>.margin-bottom
 *   (1em) + anonymous inline (\n) + <p>.margin-top (1em) renders as 2em
 *   of vertical space instead of the collapsed 1em you'd expect in a
 *   vanilla HTML document.
 *
 *   `markdown-to-jsx` (our previous engine) did not emit these whitespace
 *   text nodes, which is why pre-migration production looks natural.
 *   Removing them at the AST level is the one correct fix that restores
 *   standard block-flow margin collapsing.
 *
 * What the plugin does:
 *   Walks the hast tree and drops any `text` node whose value is entirely
 *   whitespace and whose immediate siblings on both sides are block-level
 *   elements (or tree edges). This preserves:
 *     - leading/trailing whitespace inside inline content
 *       (e.g. `<em>a</em> <em>b</em>`)
 *     - whitespace separating inline siblings within a block
 *   …and removes only the cosmetic "\n" between sibling blocks.
 */

const BLOCK_TAGS = new Set([
  'address', 'article', 'aside', 'blockquote', 'details', 'div',
  'dl', 'dd', 'dt', 'fieldset', 'figure', 'footer', 'form',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'header', 'hr', 'li', 'main', 'nav', 'ol',
  'p', 'pre', 'section', 'table', 'tbody', 'td', 'tfoot', 'th',
  'thead', 'tr', 'ul',
  // Our custom CellCog elements render as block-level chips / tiles
  'cellcog-generate-file',
  'cellcog-show-file',
]);

const isBlockOrEdge = (sibling) => {
  if (!sibling) return true; // edge of the children array counts as a boundary
  if (sibling.type !== 'element') return false;
  return BLOCK_TAGS.has(sibling.tagName);
};

const isWhitespaceText = (node) =>
  node && node.type === 'text' && typeof node.value === 'string' && /^\s*$/.test(node.value);

const scrubChildren = (node) => {
  if (!node || !Array.isArray(node.children)) return;

  node.children = node.children.filter((child, i, arr) => {
    if (!isWhitespaceText(child)) return true;
    return !(isBlockOrEdge(arr[i - 1]) && isBlockOrEdge(arr[i + 1]));
  });

  for (const child of node.children) {
    if (child && Array.isArray(child.children)) scrubChildren(child);
  }
};

const rehypeStripBlockWhitespace = () => (tree) => {
  scrubChildren(tree);
};

export default rehypeStripBlockWhitespace;
